import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Image
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { bannerService } from '../../services/bannerService';
import { programService } from '../../services/programService';

const BannerCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  // Buscar banners ativos (público)
  const { data: bannersData, isLoading: bannersLoading, error: bannersError } = useQuery({
    queryKey: ['banners-active'],
    queryFn: () => bannerService.getActiveBanners(),
    retry: (failureCount, error) => {
      // Retry apenas para erros de rede/CORS, não para erros 404/500
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false; // Não retry para erros 4xx
      }
      return failureCount < 3; // Retry até 3 vezes para erros de rede
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });

  // Buscar programas com banners (apenas ativos)
  const { data: programsData, isLoading: programsLoading, error: programsError } = useQuery({
    queryKey: ['programs-with-banners'],
    queryFn: () => programService.getActiveProgramsWithBanners(),
    retry: (failureCount, error) => {
      // Retry apenas para erros de rede/CORS, não para erros 404/500
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false; // Não retry para erros 4xx
      }
      return failureCount < 3; // Retry até 3 vezes para erros de rede
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });

  const isLoading = bannersLoading || programsLoading;
  const error = bannersError || programsError;

  // Combinar banners e programas com banners
  const banners = Array.isArray(bannersData?.data?.banners) ? bannersData.data.banners : [];
  const programs = Array.isArray(programsData?.data?.programs) ? programsData.data.programs : [];
  
  // Filtrar programas que têm banner e estão ativos
  const programsWithBanners = programs.filter(program => program.banner_image && program.is_active);
  
  // Combinar banners tradicionais com banners de programas
  const allBanners = [
    ...banners.map(banner => ({
      id: `banner-${banner.id || banner._id}`,
      title: banner.title,
      description: banner.description || '',
      image_url: banner.image_url || banner.imageUrl,
      link_url: banner.link_url || banner.linkUrl,
      type: 'banner',
      order_position: banner.order_position ?? banner.orderPosition ?? 0
    })),
    ...programsWithBanners.map(program => ({
      id: `program-${program.id}`,
      title: program.name,
      description: program.description || '',
      image_url: program.banner_image,
      link_url: null, // Programas não têm link externo
      type: 'program',
      program: program,
      order_position: 999 + program.id // Colocar programas no final
    }))
  ].sort((a, b) => a.order_position - b.order_position);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlay || allBanners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allBanners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlay, allBanners.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? allBanners.length - 1 : prev - 1
    );
    setIsAutoPlay(false); // Stop auto-play when user interacts
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allBanners.length);
    setIsAutoPlay(false); // Stop auto-play when user interacts
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress sx={{ color: '#8B5CF6' }} />
      </Box>
    );
  }

  if (error) {
    // Determinar o tipo de erro para mensagem mais específica
    let errorMessage = error.message;
    let errorTitle = 'Erro ao carregar banners';
    
    if (error.message.includes('Erro de conexão') || error.message.includes('Network Error')) {
      errorTitle = 'Problema de Conexão';
      errorMessage = 'Não foi possível conectar com o servidor. Verifique sua conexão com a internet.';
    } else if (error.message.includes('CORS')) {
      errorTitle = 'Erro de Configuração';
      errorMessage = 'Problema de configuração do servidor. Tente novamente em alguns minutos.';
    } else if (error.response?.status === 404) {
      errorTitle = 'Serviço Indisponível';
      errorMessage = 'O serviço de banners está temporariamente indisponível.';
    }
    
    return (
      <Alert 
        severity="error" 
        sx={{ 
          mb: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {errorTitle}
        </Typography>
        <Typography variant="body2">
          {errorMessage}
        </Typography>
      </Alert>
    );
  }

  if (allBanners.length === 0) {
    // Fallback: mostrar banner padrão quando não há banners
    const defaultBanner = {
      id: 'default-banner',
      title: 'NostalBeat Radio',
      description: 'Sua rádio online favorita com música nostálgica',
      image_url: null,
      type: 'default'
    };
    
    return (
      <Box
        sx={{
          height: { xs: 200, sm: 250, md: 300, lg: 400 },
          borderRadius: { xs: 2, md: 3 },
          overflow: 'hidden',
          position: 'relative',
          background: 'linear-gradient(135deg, #8B5CF6, #06B6D4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 8px 32px rgba(157, 0, 255, 0.3)',
          }
        }}
        onClick={() => window.location.reload()} // Recarregar página ao clicar
      >
        <Box sx={{ textAlign: 'center', zIndex: 2, p: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'white', 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              mb: 2,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            {defaultBanner.title}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mb: 2,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            {defaultBanner.description}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            Clique para recarregar
          </Typography>
        </Box>
      </Box>
    );
  }

  const currentBanner = allBanners[currentIndex];

  return (
    <Box
      sx={{ position: 'relative', mb: 4 }}
      onMouseEnter={() => setIsAutoPlay(false)}
      onMouseLeave={() => setIsAutoPlay(true)}
    >
      {/* Main Banner Display with smooth fade between slides */}
      <Box
        key={currentIndex}
        sx={{
          animation: 'fadeIn 400ms ease',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'scale(0.995)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
      <Card
        sx={{
          height: { 
            xs: '40vh',
            sm: 250, 
            md: 300, 
            lg: 400 
          },
          '@media (min-width: 1200px) and (max-height: 720px)': {
            height: '270px !important',
          },
          '@media (min-width: 1200px) and (min-height: 721px) and (max-height: 820px)': {
            height: '330px !important',
          },
          minHeight: { xs: 180, sm: 220 },
          maxHeight: { xs: 250, sm: 400 },
          borderRadius: { xs: 2.5, md: 4 },
          overflow: 'hidden',
          position: 'relative',
          cursor: currentBanner?.link_url ? 'pointer' : 'default',
          border: '1px solid rgba(255, 107, 53, 0.25)',
          background: 'rgba(10, 10, 14, 0.95)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
          transition: 'transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease',
          '&:hover': {
            transform: { xs: 'none', md: 'scale(1.015)' },
            borderColor: 'rgba(255, 107, 53, 0.55)',
            boxShadow: { xs: 'none', md: '0 20px 45px rgba(0, 0, 0, 0.8), 0 0 25px rgba(255, 107, 53, 0.25)' },
          },
        }}
        onClick={() => handleBannerClick(currentBanner)}
      >
        {currentBanner?.image_url ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: { xs: 'rgba(0, 0, 0, 0.05)', sm: 'transparent' },
              position: 'relative',
            }}
          >
            <CardMedia
              component="img"
              image={currentBanner.image_url}
              alt={currentBanner.title}
              decoding="async"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/img/bg.png';
              }}
              sx={{
                objectFit: { 
                  xs: 'contain', // Contain para mobile para mostrar a imagem completa
                  sm: 'cover',
                  md: 'cover'
                },
                objectPosition: { 
                  xs: 'center center', // Centralizar a imagem no mobile
                  sm: 'center center',
                  md: 'center center'
                },
                height: { 
                  xs: 'auto', // Altura automática no mobile
                  sm: '100%',
                  md: '100%'
                },
                width: { 
                  xs: '100%', // Largura total no mobile
                  sm: '100%',
                  md: '100%'
                },
                maxHeight: { 
                  xs: '100%', // Altura máxima no mobile
                  sm: '100%',
                  md: '100%'
                },
                maxWidth: { 
                  xs: '100%', // Largura máxima no mobile
                  sm: '100%',
                  md: '100%'
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #8B5CF6, #06B6D4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Image sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)' }} />
          </Box>
        )}

        {/* Overlay with Banner Info */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(10, 10, 14, 0.85))',
            p: { xs: 1.5, sm: 2, md: 2.5 },
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {currentBanner?.link_url && (
              <Chip
                label="Clique para conferir"
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 107, 53, 0.9)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.78rem' },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 10px rgba(255, 107, 53, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  letterSpacing: '0.5px',
                  '&:hover': {
                    bgcolor: '#FF6B35',
                  },
                }}
              />
            )}

            {currentBanner?.type === 'program' && (
              <Chip
                label="Programa ao Vivo"
                size="small"
                sx={{
                  bgcolor: 'rgba(0, 212, 255, 0.9)',
                  color: '#0A0A0E',
                  fontWeight: 800,
                  fontSize: { xs: '0.7rem', sm: '0.78rem' },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 10px rgba(0, 212, 255, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  letterSpacing: '0.5px',
                  '&:hover': {
                    bgcolor: '#00D4FF',
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Card>
      </Box>

      {/* Navigation Buttons */}
      {allBanners.length > 1 && (
        <>
          <IconButton
            onClick={handlePrevious}
            aria-label="Banner anterior"
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 12, md: -22 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(10, 10, 14, 0.85)',
              color: '#FF6B35',
              backdropFilter: 'blur(8px)',
              width: { xs: 36, sm: 42, md: 46 },
              height: { xs: 36, sm: 42, md: 46 },
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 107, 53, 0.25)',
              border: '1px solid rgba(255, 107, 53, 0.4)',
              transition: 'all 0.25s ease',
              '&:hover': {
                bgcolor: '#FF6B35',
                color: '#fff',
                borderColor: '#FF6B35',
                boxShadow: '0 0 20px rgba(255, 107, 53, 0.7)',
                transform: 'translateY(-50%) scale(1.08)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
              },
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={handleNext}
            aria-label="Próximo banner"
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 12, md: -22 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(10, 10, 14, 0.85)',
              color: '#FF6B35',
              backdropFilter: 'blur(8px)',
              width: { xs: 36, sm: 42, md: 46 },
              height: { xs: 36, sm: 42, md: 46 },
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 107, 53, 0.25)',
              border: '1px solid rgba(255, 107, 53, 0.4)',
              transition: 'all 0.25s ease',
              '&:hover': {
                bgcolor: '#FF6B35',
                color: '#fff',
                borderColor: '#FF6B35',
                boxShadow: '0 0 20px rgba(255, 107, 53, 0.7)',
                transform: 'translateY(-50%) scale(1.08)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}

      {/* Dynamic Pill Capsule Indicators */}
      {allBanners.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: { xs: 1.5, md: 2 }, gap: 1 }}>
          {allBanners.map((_, index) => {
            const isActive = currentIndex === index;
            return (
              <Box
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlay(false);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Ir para banner ${index + 1}`}
                sx={{
                  width: isActive ? { xs: 24, sm: 32 } : { xs: 8, sm: 8 },
                  height: { xs: 6, sm: 7 },
                  borderRadius: '10px',
                  bgcolor: isActive ? '#FF6B35' : 'rgba(255, 255, 255, 0.25)',
                  boxShadow: isActive ? '0 0 10px rgba(255, 107, 53, 0.7)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: isActive ? '#FF6B35' : 'rgba(255, 255, 255, 0.55)',
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Auto-play Indicator removed as requested */}
    </Box>
  );
};

export default BannerCarousel;
